import {
  ChatMessage,
  PhoneCallItem,
  PhoneCallMessage,
  PhoneChatItem,
  PhoneChatReplyOption,
} from '../../types';
import { applyAutoConvergeToPhoneReplies } from '../../systems/phone-branch';

export type PhoneConvMessage = {
  kind: 'message';
  sender: 'contact' | 'player';
  text: string;
  time?: string;
};

export type PhoneConvChoice = {
  kind: 'choice';
  options: PhoneConvBranch[];
};

export type PhoneConvBranch = {
  label: string;
  nodes: PhoneConvNode[];
};

export type PhoneConvNode = PhoneConvMessage | PhoneConvChoice;

export type PhoneConvEditModel = {
  preamble: PhoneConvMessage[];
  choice: PhoneConvChoice | null;
};

export type PhoneConversationItem = PhoneCallItem | PhoneChatItem;

function messageToConv(msg: ChatMessage | PhoneCallMessage): PhoneConvMessage {
  return {
    kind: 'message',
    sender: msg.sender,
    text: msg.text,
    ...('time' in msg && msg.time ? { time: msg.time } : {}),
  };
}

function optionToBranch(opt: PhoneChatReplyOption): PhoneConvBranch {
  const nodes: PhoneConvNode[] = [];
  if (opt.contactReply?.trim()) {
    nodes.push({ kind: 'message', sender: 'contact', text: opt.contactReply });
  }
  if (opt.replyOptions?.length) {
    nodes.push({
      kind: 'choice',
      options: opt.replyOptions.map(optionToBranch),
    });
  }
  return { label: opt.text, nodes };
}

function branchToOption(branch: PhoneConvBranch): PhoneChatReplyOption {
  let contactReply: string | undefined;
  let replyOptions: PhoneChatReplyOption[] | undefined;

  for (const node of branch.nodes) {
    if (node.kind === 'message' && node.sender === 'contact') {
      if (!contactReply) contactReply = node.text.trim() || undefined;
    } else if (node.kind === 'choice' && node.options.length > 0) {
      replyOptions = node.options.map(branchToOption);
    }
  }

  return {
    text: branch.label,
    contactReply,
    replyOptions: replyOptions?.length ? replyOptions : undefined,
  };
}

function convToMessage(msg: PhoneConvMessage, isSms: boolean): ChatMessage | PhoneCallMessage {
  if (isSms) {
    return {
      sender: msg.sender,
      text: msg.text,
      ...(msg.time ? { time: msg.time } : {}),
    };
  }
  return { sender: msg.sender, text: msg.text };
}

export function toEditModel(item: PhoneConversationItem): PhoneConvEditModel {
  return {
    preamble: item.messages.map(messageToConv),
    choice: item.replyOptions?.length
      ? { kind: 'choice', options: item.replyOptions.map(optionToBranch) }
      : null,
  };
}

export function fromEditModel(
  item: PhoneConversationItem,
  model: PhoneConvEditModel,
  convergeSceneId?: string
): PhoneConversationItem {
  const isSms = item.type === 'phoneChat';
  const messages = model.preamble.map((m) => convToMessage(m, isSms));
  let replyOptions = model.choice?.options.length
    ? model.choice.options.map(branchToOption)
    : undefined;

  if (convergeSceneId && replyOptions?.length) {
    replyOptions = applyAutoConvergeToPhoneReplies(replyOptions, convergeSceneId);
  }

  return {
    ...item,
    messages,
    replyOptions: replyOptions?.length ? replyOptions : undefined,
  };
}

export function patchEditModel(
  item: PhoneConversationItem,
  patch: Partial<PhoneConvEditModel>,
  convergeSceneId?: string
): PhoneConversationItem {
  const current = toEditModel(item);
  return fromEditModel(item, { ...current, ...patch }, convergeSceneId);
}
